import React, { useState, useEffect, useCallback, useMemo, memo, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import { List } from 'react-window';
import { authenticatedApiCall } from '../../utils/apiClient';
import FilterDropdown from '../common/FilterDropdown';
import ActualSpentExportModal from './ActualSpentExportModal';
import WeeklyTimesheetModal from './WeeklyTimesheetModal';
import DailyTimesheetModal from './DailyTimesheetModal';
import ActualSpentDashboard from './ActualSpentDashboard';
import CrewManagementModal from './CrewManagementModal';
import OpCodesAdmin from './OpCodesAdmin';
import EmployeesAdmin from './EmployeesAdmin';
import ExtrasAdmin from './ExtrasAdmin';
import TimesheetReports from './TimesheetReports';
import PayrollPeriodsAdmin from './PayrollPeriodsAdmin';
import TimeTrackerHelpModal from './TimeTrackerHelpModal';
import TimeTrackerAdminHelpModal from './TimeTrackerAdminHelpModal';
import TimeTrackerInteractiveTour from './TimeTrackerInteractiveTour';
import TimeTrackerAuditLog from './TimeTrackerAuditLog';
import useResizableColumns from '../../hooks/useResizableColumns';
import useTableFilter from '../../hooks/useTableFilter';
import useTableSort from '../../hooks/useTableSort';
import { useToast } from '../../hooks/useToast';
import usePresence from '../../hooks/usePresence';
import ActiveViewers from '../Matrix/ActiveViewers';

// Column definitions for the actual spent table
const COLUMNS = [
  { key: 'status', label: 'Status', type: 'status', width: 100 },
  { key: 'date', label: 'Date', type: 'date', width: 100 },
  { key: 'empl_code', label: 'Emp Code', type: 'text', width: 90 },
  { key: 'empl_name', label: 'Employee Name', type: 'text', width: 150 },
  { key: 'trade', label: 'Trade', type: 'text', width: 100 },
  { key: 'job_number', label: 'Project #', type: 'text', width: 90 },
  { key: 'op_code', label: 'Op Code', type: 'text', width: 100 },
  { key: 'shift', label: 'Shift', type: 'text', width: 60 },
  { key: 'reg', label: 'Reg', type: 'number', width: 60 },
  { key: 't12', label: 'T12', type: 'number', width: 60 },
  { key: 'dbl', label: 'DBL', type: 'number', width: 60 },
  { key: 'total_hrs', label: 'Total Hrs', type: 'number', width: 80 },
  { key: 'travel_2', label: 'Trv$', type: 'number', width: 60 },
  { key: 'meals', label: 'Meal$', type: 'number', width: 70 },
  { key: 'height', label: 'Height', type: 'number', width: 70 },
  { key: 'r_and_b', label: 'R&B', type: 'number', width: 70 },
];

// Custom render for status badges and number formatting
const renderActualSpentValue = (val, column) => {
  if (column.type === 'status' && val) {
    const isDraft = val === 'draft';
    return (
      <span style={{
        padding: '2px 8px',
        borderRadius: '12px',
        fontSize: '0.75rem',
        fontWeight: '500',
        backgroundColor: isDraft ? '#fff3cd' : '#d4edda',
        color: isDraft ? '#856404' : '#155724'
      }}>
        {isDraft ? 'Draft' : 'Submitted'}
      </span>
    );
  }
  return null; // Use default formatting
};

const formatActualSpentValue = (val, column) => {
  if (val === null || val === undefined || val === '') return '-';
  if (column.type === 'number' || column.type === 'computed') {
    const num = parseFloat(val);
    return isNaN(num) ? val : num.toFixed(2);
  }
  if (column.type === 'date' && val) {
    try {
      // Extract YYYY-MM-DD from the date string directly to avoid timezone conversion
      // Handles both "2026-01-25" and "2026-01-25T00:00:00.000Z" formats
      const dateStr = String(val).split('T')[0];
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        return dateStr;
      }
      // Fallback: try parsing as Date but use UTC components
      const date = new Date(val);
      if (!isNaN(date.getTime())) {
        const year = date.getUTCFullYear();
        const month = String(date.getUTCMonth() + 1).padStart(2, '0');
        const day = String(date.getUTCDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      }
    } catch {
      return val;
    }
  }
  return val; // Return actual value for text columns
};

/**
 * Virtualized Row Component for react-window v2.x
 * Props come from rowProps, plus index and style from List
 */
const VirtualRow = memo(({ index, style, rows, columns, onDeleteRow, columnWidths }) => {
  const item = rows[index];
  if (!item) return null;
  
  const rowBgColor = index % 2 === 0 ? 'white' : '#f8f9fa';
  
  return (
    <div 
      style={{
        ...style,
        display: 'flex',
        alignItems: 'stretch',
        backgroundColor: rowBgColor,
        borderBottom: '1px solid #dee2e6',
      }}
    >
      {/* Delete Button Column */}
      <div style={{ 
        width: 50, 
        minWidth: 50, 
        maxWidth: 50,
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        borderRight: '1px solid #eee',
        backgroundColor: rowBgColor,
        padding: '4px 8px',
        boxSizing: 'border-box',
      }}>
        <button
          onClick={() => onDeleteRow(item.id)}
          style={{
            padding: '4px 8px',
            backgroundColor: '#dc3545',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '0.75rem'
          }}
        >
          ✕
        </button>
      </div>
      
      {/* Data Columns (Read-Only) */}
      {columns.map((col) => {
        const colWidth = columnWidths?.[col.key] || col.width;
        const cellValue = item[col.key];
        // Use custom render for status badges, otherwise format the value
        const customRender = renderActualSpentValue(cellValue, col);
        const displayValue = customRender !== null ? customRender : formatActualSpentValue(cellValue, col);
        return (
          <div 
            key={col.key}
            style={{
              flex: `${colWidth} 0 ${colWidth}px`,
              minWidth: colWidth,
              padding: '4px 8px',
              display: 'flex',
              alignItems: 'center',
              borderRight: '1px solid #eee',
              boxSizing: 'border-box',
              overflow: 'hidden',
              fontSize: '0.85rem',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
            }}
            title={typeof displayValue === 'string' ? displayValue : ''}
          >
            {displayValue}
          </div>
        );
      })}
    </div>
  );
}, (prev, next) => {
  // Custom equality check for performance
  return (
    prev.index === next.index &&
    prev.style === next.style &&
    prev.rows === next.rows &&
    prev.columns === next.columns &&
    prev.columnWidths === next.columnWidths
  );
});

const ActualSpentLog = ({ projectId, jobNumber, onFilterStateChange }) => {
  const { instance, accounts } = useMsal();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const { toast, showToast } = useToast();
  const [showExportModal, setShowExportModal] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportStatus, setExportStatus] = useState(null);
  const [exportStatusType, setExportStatusType] = useState('info');
  const [showWeeklyModal, setShowWeeklyModal] = useState(false);
  const [showDailyModal, setShowDailyModal] = useState(false);
  const [showDashboard, setShowDashboard] = useState(false);
  const [showCrewModal, setShowCrewModal] = useState(false);
  const [showAdminModal, setShowAdminModal] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [showAdminHelpModal, setShowAdminHelpModal] = useState(false);
  const [interactiveTour, setInteractiveTour] = useState(null);
  const [adminView, setAdminView] = useState('menu'); // 'menu' | 'opCodes' | 'employees' | 'extras' | 'reports' | 'periods' | 'audit'
  const [showAuditModal, setShowAuditModal] = useState(false);
  const [accessLevel, setAccessLevel] = useState('self');
  const [canManageTimeTrackerAdmin, setCanManageTimeTrackerAdmin] = useState(false);
  const [canViewTimeTrackerAudit, setCanViewTimeTrackerAudit] = useState(false);
  const [canUseWeeklyTimesheet, setCanUseWeeklyTimesheet] = useState(false);
  const [projects, setProjects] = useState([]);

  // Live data push: ref + callback for real-time refresh
  const fetchDataRef = useRef(null);

  const handleDataChange = useCallback(() => {
    if (fetchDataRef.current) fetchDataRef.current();
  }, []);

  // Real-time presence tracking (shows who's viewing this page)
  // Use 'all' as identifier when viewing all projects
  const presenceId = projectId || 'all';
  const { viewers, isConnected, connectionState, socketId } = usePresence('actualSpent', presenceId, true, handleDataChange);

  const socketIdRef = useRef(null);
  useEffect(() => { socketIdRef.current = socketId; }, [socketId]);

  const syncHeaders = useCallback(() => {
    return socketIdRef.current ? { headers: { 'x-socket-id': socketIdRef.current } } : {};
  }, []);
  
  // Use reusable hooks for filtering and sorting
  const filterHook = useTableFilter({ columns: COLUMNS, items: data });
  const sortHook = useTableSort({ columns: COLUMNS });

  // Resizable columns
  const defaultWidths = useMemo(() => {
    const widths = {};
    COLUMNS.forEach(col => { widths[col.key] = col.width; });
    return widths;
  }, []);
  const { getColumnWidth, getResizeHandleProps } = useResizableColumns(
    `actualSpentLog_${projectId || 'all'}_columnWidths`,
    defaultWidths
  );

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Use /all endpoint when no projectId, otherwise use project-specific endpoint
      const endpoint = projectId 
        ? `/actual-spent/project/${projectId}?showDrafts=true`
        : '/actual-spent/all?showDrafts=true';
      
      const response = await authenticatedApiCall(
        instance,
        accounts,
        'GET',
        endpoint
      );

      if (response.data.success) {
        setData(response.data.data || []);
        setAccessLevel(response.data.accessLevel || 'self');
        setCanManageTimeTrackerAdmin(response.data.canManageTimeTrackerAdmin === true);
        setCanViewTimeTrackerAudit(response.data.canViewTimeTrackerAudit === true);
        setCanUseWeeklyTimesheet(response.data.canUseWeeklyTimesheet === true);
      } else {
        throw new Error(response.data.message || 'Failed to fetch data');
      }
    } catch (err) {
      console.error('Error fetching actual spent data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [instance, accounts, projectId]);

  useEffect(() => { fetchDataRef.current = fetchData; }, [fetchData]);

  useEffect(() => {
    // Fetch data whether projectId is set or not (null = all projects)
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const onTour = (e) => {
      const variant = e.detail?.variant || 'field';
      if (variant === 'admin') setShowAdminModal(true);
      setInteractiveTour(variant);
    };
    window.addEventListener('tt:startInteractiveTour', onTour);
    return () => window.removeEventListener('tt:startInteractiveTour', onTour);
  }, []);

  // Fetch projects list for export modal job filter
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await authenticatedApiCall(instance, accounts, 'GET', '/projects');
        if (response.data.projects) {
          setProjects(response.data.projects);
        }
      } catch (error) {
        console.error('Error fetching projects:', error);
      }
    };
    fetchProjects();
  }, [instance, accounts]);

  const handleDeleteRow = useCallback(async (rowId) => {
    if (!window.confirm('Are you sure you want to delete this record?')) return;

    try {
      const response = await authenticatedApiCall(
        instance,
        accounts,
        'DELETE',
        `/actual-spent/${rowId}`,
        null,
        syncHeaders()
      );

      if (response.data.success) {
        setData(prev => prev.filter(item => item.id !== rowId));
        showToast('Record deleted successfully', 'success');
      } else {
        throw new Error(response.data.message || 'Delete failed');
      }
    } catch (err) {
      console.error('Error deleting record:', err);
      showToast(`Delete failed: ${err.message}`, 'error');
    }
  }, [instance, accounts, showToast]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && filterHook.openFilterColumn) {
        filterHook.setOpenFilterColumn(null);
        filterHook.setFilterSearch('');
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [filterHook.openFilterColumn, filterHook.setOpenFilterColumn, filterHook.setFilterSearch]);

  // Combined active filters check
  const hasActiveFilters = filterHook.hasActiveFilters || searchTerm;

  // Clear all filters including search
  const handleClearAll = useCallback(() => {
    filterHook.clearAllFilters();
    setSearchTerm('');
    sortHook.clearSort();
    showToast('All filters cleared', 'success');
  }, [filterHook.clearAllFilters, sortHook.clearSort, showToast]);

  // Report filter state to parent (for display in project header)
  useEffect(() => {
    if (onFilterStateChange) {
      onFilterStateChange({
        hasActiveFilters,
        searchQuery: searchTerm,
        activeFilters: filterHook.activeFilters.map(f => ({
          columnName: f.label,
          type: f.type,
          count: f.count,
          mode: f.mode,
          value: f.value
        })),
        onClearAll: handleClearAll
      });
    }
    // Cleanup on unmount
    return () => {
      if (onFilterStateChange) {
        onFilterStateChange(null);
      }
    };
  }, [hasActiveFilters, searchTerm, filterHook.activeFilters, handleClearAll, onFilterStateChange]);

  // Export handlers
  const handleExport = async (filters = {}) => {
    try {
      setIsExporting(true);
      setExportStatus('Starting Maestro payroll export...');
      setExportStatusType('info');

      const response = await authenticatedApiCall(
        instance,
        accounts,
        'POST',
        '/actual-spent/export',
        { 
          projectId,
          startDate: filters.startDate || null,
          endDate: filters.endDate || null,
          jobNumber: filters.jobNumber !== 'all' ? filters.jobNumber : null
        },
        syncHeaders()
      );

      if (response.data.success) {
        setExportStatus(`Maestro payroll export started! The file will download automatically when ready.`);
        pollJobStatus(response.data.jobId);
      } else {
        throw new Error(response.data.message || 'Failed to start Maestro payroll export');
      }
    } catch (error) {
      console.error('Export error:', error);
      setExportStatus(`Error: ${error.message}`);
      setExportStatusType('error');
      setIsExporting(false);
    }
  };

  const pollJobStatus = async (jobId) => {
    let attempts = 0;
    const MAX_ATTEMPTS = 180; // 3 minutes at 1s intervals

    const checkStatus = async () => {
      attempts++;
      if (attempts > MAX_ATTEMPTS) {
        setExportStatus('Export timed out after 3 minutes. Please contact support if the file was not delivered.');
        setExportStatusType('error');
        setIsExporting(false);
        return;
      }

      try {
        const response = await authenticatedApiCall(
          instance,
          accounts,
          'GET',
          `/job-status/${jobId}`
        );

        const job = response.data.data || response.data;

        if (job.status === 'completed') {
          setExportStatus('Maestro payroll export completed! Downloading...');
          setExportStatusType('success');
          
          const downloadResponse = await authenticatedApiCall(
            instance,
            accounts,
            'GET',
            `/download/${jobId}`,
            null,
            { responseType: 'blob' }
          );
          
          const blob = new Blob([downloadResponse.data], { 
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
          });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `maestro_payroll_export.xlsx`;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          a.remove();
          
          setExportStatus('Download complete!');
          setIsExporting(false);
          setTimeout(() => {
            setShowExportModal(false);
            setExportStatus(null);
          }, 1500);
        } else if (job.status === 'failed') {
          setExportStatus(`Maestro payroll export failed: ${job.error_message || 'Unknown error'}`);
          setExportStatusType('error');
          setIsExporting(false);
        } else {
          setExportStatus(`Processing... ${job.processed_rows || 0} rows (${attempts}/${MAX_ATTEMPTS})`);
          setTimeout(checkStatus, 1000);
        }
      } catch (error) {
        console.error('Status check error:', error);
        setExportStatus(`Error checking status: ${error.message}`);
        setExportStatusType('error');
        setIsExporting(false);
      }
    };

    checkStatus();
  };

  const handleExportModalClose = () => {
    if (!isExporting) {
      setShowExportModal(false);
      setExportStatus(null);
      setExportStatusType('info');
    }
  };

  const filteredData = useMemo(() => {
    let result = [...data];
    
    // Global search
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      result = result.filter(item => COLUMNS.some(col => { 
        const val = item[col.key]; 
        return val !== null && val !== undefined && String(val).toLowerCase().includes(search); 
      }));
    }
    
    // Apply column filters
    result = filterHook.applyFilters(result);
    
    // Apply sorting
    result = sortHook.applySorting(result);
    
    return result;
  }, [data, searchTerm, filterHook.applyFilters, sortHook.applySorting]);

  // Calculate column widths for virtualized table
  const columnWidths = useMemo(() => {
    const widths = {};
    COLUMNS.forEach(col => {
      widths[col.key] = getColumnWidth(col.key, col.width);
    });
    return widths;
  }, [getColumnWidth]);

  // Calculate total table width for horizontal scrolling
  const totalTableWidth = useMemo(() => {
    return 50 + COLUMNS.reduce((sum, col) => sum + (columnWidths[col.key] || col.width), 0);
  }, [columnWidths]);

  // Memoized row props for react-window v2.x API
  const rowProps = useMemo(() => ({
    rows: filteredData,
    columns: COLUMNS,
    onDeleteRow: handleDeleteRow,
    columnWidths,
  }), [filteredData, handleDeleteRow, columnWidths]);

  // Table dimensions
  const tableHeight = Math.min(Math.max(400, window.innerHeight - 350), 600);
  const headerHeight = 40;
  const rowHeight = 36;
  const scrollbarWidth = 12; // matches ::-webkit-scrollbar width in styles below
  const hasVerticalScrollbar = filteredData.length * rowHeight > tableHeight;

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '3rem' }}>
        <div style={{ 
          width: '40px', 
          height: '40px', 
          border: '4px solid #f3f3f3',
          borderTop: '4px solid #007bff',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '1rem', backgroundColor: '#f8d7da', borderRadius: '8px', color: '#721c24' }}>
        <strong>⚠️ Error:</strong> {error}
        <button onClick={fetchData} style={{ marginLeft: '1rem', padding: '0.5rem 1rem', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div style={{ padding: '1rem', backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      {/* Toast Notification */}
      {toast.show && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          padding: '1rem 1.5rem',
          borderRadius: '8px',
          backgroundColor: toast.type === 'success' ? '#28a745' : '#dc3545',
          color: 'white',
          zIndex: 1000,
          boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
          animation: 'slideIn 0.3s ease'
        }}>
          {toast.message}
        </div>
      )}

      {/* Header - Action buttons only */}
      <div style={{
        backgroundColor: 'white',
        padding: '0.75rem 1rem',
        borderRadius: '8px',
        marginBottom: '1rem',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: '0.5rem',
        flexWrap: 'nowrap',
        overflowX: 'auto'
      }}>
        {/* Active Viewers */}
        <ActiveViewers
          viewers={viewers}
          isConnected={isConnected}
          connectionState={connectionState}
          maxVisible={4}
          showConnectionStatus={true}
        />
        
        {/* Search */}
        <div style={{ position: 'relative' }} data-tt-tour="search">
          <input
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              padding: '6px 10px 6px 32px',
              border: '1px solid #dee2e6',
              borderRadius: '4px',
              width: '160px',
              fontSize: '0.85rem'
            }}
          />
          <span style={{
            position: 'absolute',
            left: '10px',
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#6c757d',
            fontSize: '0.8rem'
          }}>🔍</span>
        </div>

        <button
          data-tt-tour="help"
          onClick={() => setShowHelpModal(true)}
          style={{ padding: '0.5rem 0.75rem', backgroundColor: '#0d9488', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: '600', whiteSpace: 'nowrap' }}
        >
          ❓ Help
        </button>
        {canViewTimeTrackerAudit && (
          <button
            data-tt-tour="audit"
            onClick={() => setShowAuditModal(true)}
            style={{ padding: '0.5rem 0.75rem', backgroundColor: '#fff7ed', color: '#9a3412', border: '1px solid #fdba74', borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: '600', whiteSpace: 'nowrap' }}
          >
            Audit
          </button>
        )}
        {canManageTimeTrackerAdmin && (
          <button data-tt-tour="admin" onClick={() => setShowAdminModal(true)} style={{ padding: '0.5rem 0.75rem', backgroundColor: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: '500', whiteSpace: 'nowrap' }}>⚙️ Admin</button>
        )}
        <button data-tt-tour="crews" onClick={() => setShowCrewModal(true)} style={{ padding: '0.5rem 0.75rem', backgroundColor: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: '500', whiteSpace: 'nowrap' }}>👥 Crews</button>
        <button data-tt-tour="daily" onClick={() => setShowDailyModal(true)} style={{ padding: '0.5rem 0.75rem', backgroundColor: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: '500', whiteSpace: 'nowrap' }}>➕ Daily</button>
        {canUseWeeklyTimesheet && (
          <button data-tt-tour="weekly" onClick={() => setShowWeeklyModal(true)} style={{ padding: '0.5rem 0.75rem', backgroundColor: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: '500', whiteSpace: 'nowrap' }}>➕ Weekly</button>
        )}
        <button data-tt-tour="analytics" onClick={() => setShowDashboard(true)} style={{ padding: '0.5rem 0.75rem', backgroundColor: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: '500', whiteSpace: 'nowrap' }}>📊 Analytics</button>
      </div>

      {/* Virtualized Table */}
      <div 
        className="virtualized-table-container"
        style={{ 
          backgroundColor: 'white', 
          borderRadius: '8px', 
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          border: '1px solid #dee2e6',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: 'calc(100vh - 280px)',
        }}
      >
        <div style={{ overflowX: 'auto', overflowY: 'auto', flex: 1 }}>
          <div style={{ width: '100%', minWidth: totalTableWidth }}>
            {/* Header Row */}
            <div style={{ 
              display: 'flex', 
              backgroundColor: '#f8f9fa', 
              color: '#495057',
              position: 'sticky', 
              top: 0, 
              zIndex: 10,
              height: headerHeight,
              borderBottom: '1px solid #dee2e6',
              paddingRight: hasVerticalScrollbar ? scrollbarWidth : 0,
            }}>
              {/* Delete Column Header */}
              <div style={{ 
                width: 50, 
                minWidth: 50, 
                maxWidth: 50,
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                borderRight: '1px solid #dee2e6',
                padding: '4px 8px',
                boxSizing: 'border-box',
                fontWeight: '600',
                fontSize: '0.85rem',
              }}>
                🗑️
              </div>
              
              {/* Column Headers */}
              {COLUMNS.map(col => {
                const colWidth = columnWidths[col.key] || col.width;
                const hasFilter = filterHook.hasColumnFilter(col.key);
                return (
                  <div 
                    key={col.key}
                    style={{
                      flex: `${colWidth} 0 ${colWidth}px`,
                      minWidth: colWidth,
                      padding: '4px 8px',
                      display: 'flex',
                      alignItems: 'center',
                      borderRight: '1px solid #eee',
                      boxSizing: 'border-box',
                      overflow: 'hidden',
                      cursor: 'pointer',
                      userSelect: 'none',
                      position: 'relative',
                      fontSize: '0.85rem',
                      fontWeight: '600',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <span 
                      onClick={() => sortHook.handleSort(col.key)} 
                      style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}
                      title={`Click to sort by ${col.label}`}
                    >
                      {col.label}
                      {sortHook.sortColumn === col.key && (
                        <span style={{ marginLeft: '4px' }}>
                          {sortHook.sortDirection === 'asc' ? '▲' : '▼'}
                        </span>
                      )}
                    </span>
                    <span 
                      onClick={(e) => { 
                        e.stopPropagation(); 
                        filterHook.setFilterSearch(''); 
                        filterHook.setOpenFilterColumn(filterHook.openFilterColumn === col.key ? null : col.key); 
                      }} 
                      style={{ 
                        cursor: 'pointer', 
                        opacity: hasFilter ? 1 : 0.5, 
                        fontSize: '0.75rem',
                        marginLeft: '4px',
                        color: hasFilter ? '#10b981' : 'white',
                      }} 
                      title="Filter"
                    >
                      🔽
                    </span>
                    {/* Resize Handle */}
                    <div {...getResizeHandleProps(col.key, colWidth)} />
                    
                    {/* Filter Dropdown */}
                    {filterHook.openFilterColumn === col.key && (
                      <FilterDropdown
                        column={col}
                        uniqueValues={filterHook.getUniqueValues(col.key)}
                        selectedValues={filterHook.selectedValues[col.key] || []}
                        textFilter={filterHook.textFilters[col.key] || null}
                        numericFilter={filterHook.numericFilters[col.key] || null}
                        dateFilter={filterHook.dateFilters[col.key] || null}
                        filterSearch={filterHook.filterSearch}
                        onToggleValue={filterHook.toggleValue}
                        onSelectAll={filterHook.selectAll}
                        onClearColumn={filterHook.clearColumn}
                        onApplyTextFilter={filterHook.applyTextFilter}
                        onApplyNumericFilter={filterHook.applyNumericFilter}
                        onApplyDateFilter={filterHook.applyDateFilter}
                        onClose={() => {
                          filterHook.setOpenFilterColumn(null);
                          filterHook.setFilterSearch('');
                        }}
                        onSearchChange={filterHook.setFilterSearch}
                      />
                    )}
                  </div>
                );
              })}
            </div>
            
            {/* Virtualized Body */}
            <div style={{ height: tableHeight }}>
              {filteredData.length === 0 ? (
                <div style={{ padding: '3rem', textAlign: 'center', color: '#6c757d' }}>
                  {searchTerm ? 'No matching records found' : (canUseWeeklyTimesheet ? 'No records. Click "+ Daily" or "+ Weekly" to enter time.' : 'No records. Click "+ Daily" to enter time.')}
                </div>
              ) : (
                <List
                  key={`${filteredData.length}-${Object.keys(filterHook.selectedValues).length}-${Object.keys(filterHook.textFilters).length}`}
                  rowComponent={VirtualRow}
                  rowCount={filteredData.length}
                  rowHeight={rowHeight}
                  rowProps={rowProps}
                  overscanCount={10}
                  style={{ height: tableHeight, width: '100%', overflowX: 'hidden' }}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes slideIn {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        .virtualized-table-container { scrollbar-width: thin; scrollbar-color: #c1c1c1 #f1f1f1; }
        .virtualized-table-container::-webkit-scrollbar { width: 12px; height: 12px; }
        .virtualized-table-container::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 4px; }
        .virtualized-table-container::-webkit-scrollbar-thumb { background: #c1c1c1; border-radius: 4px; }
        .virtualized-table-container::-webkit-scrollbar-thumb:hover { background: #a1a1a1; }
      `}</style>

      {/* Export Modal */}
      {showExportModal && (
        <ActualSpentExportModal
          isOpen={showExportModal}
          onClose={handleExportModalClose}
          onExport={handleExport}
          isExporting={isExporting}
          exportStatus={exportStatus}
          statusType={exportStatusType}
          projects={projects}
        />
      )}

      {/* Upload Modal */}

      {/* Weekly Timesheet Modal */}
      {showWeeklyModal && canUseWeeklyTimesheet && (
        <WeeklyTimesheetModal
          projectId={projectId}
          jobNumber={jobNumber}
          onClose={() => setShowWeeklyModal(false)}
          onSubmitComplete={() => {
            fetchData();
            setShowWeeklyModal(false);
          }}
          isElevated={accessLevel === 'all' || accessLevel === 'team'}
        />
      )}

      {/* Daily Timesheet Modal */}
      {showDailyModal && (
        <DailyTimesheetModal
          projectId={projectId}
          jobNumber={jobNumber}
          canUseWeeklyTimesheet={canUseWeeklyTimesheet}
          onClose={() => setShowDailyModal(false)}
          onSubmitComplete={() => {
            fetchData();
            setShowDailyModal(false);
          }}
        />
      )}

      {/* Analytics Dashboard Modal */}
      {showDashboard && (
        <ActualSpentDashboard
          data={data}
          onClose={() => setShowDashboard(false)}
        />
      )}

      {/* Crew Management Modal */}
      {showCrewModal && (
        <CrewManagementModal
          projectId={projectId}
          onClose={() => setShowCrewModal(false)}
        />
      )}

      {/* Admin Modal with Navigation */}
      {showAdminModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            width: '90%',
            maxWidth: (adminView === 'opCodes' || adminView === 'employees' || adminView === 'extras' || adminView === 'reports' || adminView === 'periods' || adminView === 'audit') ? '1000px' : '800px',
            maxHeight: '85vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
          }}>
            {/* Render based on adminView */}
            {adminView === 'opCodes' ? (
              <OpCodesAdmin onBack={() => setAdminView('menu')} />
            ) : adminView === 'employees' ? (
              <EmployeesAdmin onBack={() => setAdminView('menu')} />
            ) : adminView === 'extras' ? (
              <ExtrasAdmin onBack={() => setAdminView('menu')} />
            ) : adminView === 'reports' ? (
              <TimesheetReports onBack={() => setAdminView('menu')} />
            ) : adminView === 'audit' ? (
              <TimeTrackerAuditLog onBack={() => setAdminView('menu')} />
            ) : adminView === 'periods' ? (
              <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '1rem 1.5rem', borderBottom: '1px solid #e5e7eb', backgroundColor: '#f9fafb' }}>
                  <button onClick={() => setAdminView('menu')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6366f1', fontWeight: 600, fontSize: '0.9rem' }}>← Back</button>
                  <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600 }}>🔒 Period Lock Management</h2>
                </div>
                <div style={{ flex: 1, overflowY: 'auto' }}>
                  <PayrollPeriodsAdmin />
                </div>
              </div>
            ) : (
              <>
                {/* Menu View Header */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '1rem 1.5rem',
                  borderBottom: '1px solid #e5e7eb',
                  backgroundColor: '#f9fafb'
                }}>
                  <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '600' }}>
                    Time Tracker Admin
                  </h2>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <button
                    type="button"
                    onClick={() => setShowAdminHelpModal(true)}
                    style={{
                      padding: '6px 10px', backgroundColor: '#0f766e', color: 'white',
                      border: 'none', borderRadius: '6px', cursor: 'pointer',
                      fontSize: '0.8rem', fontWeight: 600,
                    }}
                  >
                    ❓ Help
                  </button>
                  <button
                    onClick={() => { setShowAdminModal(false); setAdminView('menu'); }}
                    style={{
                      background: 'none',
                      border: 'none',
                      fontSize: '1.5rem',
                      cursor: 'pointer',
                      color: '#6b7280'
                    }}
                  >
                    ×
                  </button>
                  </div>
                </div>
                
                {/* Menu Content */}
                <div style={{ padding: '1.5rem', overflowY: 'auto', flex: 1 }}>
                  <p style={{ color: '#6b7280', marginBottom: '1rem' }} data-tt-tour="admin-home">
                    Select an admin option to configure Time Tracker settings.
                  </p>
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                    gap: '1rem' 
                  }}>
                    {/* Op Codes - Clickable */}
                    <div 
                      data-tt-tour="admin-opcodes"
                      onClick={() => setAdminView('opCodes')}
                      style={{ 
                        padding: '1rem', 
                        backgroundColor: '#f3f4f6', 
                        borderRadius: '8px',
                        border: '1px solid #e5e7eb',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#e5e7eb';
                        e.currentTarget.style.borderColor = '#6366f1';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#f3f4f6';
                        e.currentTarget.style.borderColor = '#e5e7eb';
                      }}
                    >
                      <h4 style={{ margin: '0 0 0.5rem 0', color: '#374151' }}>Op Codes</h4>
                      <p style={{ margin: 0, fontSize: '0.875rem', color: '#6b7280' }}>
                        Manage operation codes and descriptions
                      </p>
                    </div>
                    
                    {/* Employees - Clickable */}
                    <div 
                      data-tt-tour="admin-employees"
                      onClick={() => setAdminView('employees')}
                      style={{ 
                        padding: '1rem', 
                        backgroundColor: '#f3f4f6', 
                        borderRadius: '8px',
                        border: '1px solid #e5e7eb',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#e5e7eb';
                        e.currentTarget.style.borderColor = '#6366f1';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#f3f4f6';
                        e.currentTarget.style.borderColor = '#e5e7eb';
                      }}
                    >
                      <h4 style={{ margin: '0 0 0.5rem 0', color: '#374151' }}>Employees</h4>
                      <p style={{ margin: 0, fontSize: '0.875rem', color: '#6b7280' }}>
                        Manage employee records and assignments
                      </p>
                    </div>
                    
                    {/* Extras Management - Clickable */}
                    <div 
                      data-tt-tour="admin-extras"
                      onClick={() => setAdminView('extras')}
                      style={{ 
                        padding: '1rem', 
                        backgroundColor: '#f3f4f6', 
                        borderRadius: '8px',
                        border: '1px solid #e5e7eb',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#e5e7eb';
                        e.currentTarget.style.borderColor = '#f59e0b';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#f3f4f6';
                        e.currentTarget.style.borderColor = '#e5e7eb';
                      }}
                    >
                      <h4 style={{ margin: '0 0 0.5rem 0', color: '#374151' }}>Extras</h4>
                      <p style={{ margin: 0, fontSize: '0.875rem', color: '#6b7280' }}>
                        Create and manage extra op codes per discipline
                      </p>
                    </div>
                    
                    {/* Reports */}
                    <div
                      data-tt-tour="admin-reports"
                      onClick={() => setAdminView('reports')}
                      style={{
                        padding: '1rem',
                        backgroundColor: '#f3f4f6',
                        borderRadius: '8px',
                        border: '1px solid #e5e7eb',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#e5e7eb';
                        e.currentTarget.style.borderColor = '#6366f1';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#f3f4f6';
                        e.currentTarget.style.borderColor = '#e5e7eb';
                      }}
                    >
                      <h4 style={{ margin: '0 0 0.5rem 0', color: '#374151' }}>📄 Reports</h4>
                      <p style={{ margin: 0, fontSize: '0.875rem', color: '#6b7280' }}>
                        Generate payroll PDF reports (Details, Master, OP Code Summary, Contract)
                      </p>
                    </div>
                    
                    {/* Period Lock */}
                    <div
                      data-tt-tour="admin-periods"
                      onClick={() => setAdminView('periods')}
                      style={{
                        padding: '1rem',
                        backgroundColor: '#f3f4f6',
                        borderRadius: '8px',
                        border: '1px solid #e5e7eb',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#e5e7eb';
                        e.currentTarget.style.borderColor = '#dc3545';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#f3f4f6';
                        e.currentTarget.style.borderColor = '#e5e7eb';
                      }}
                    >
                      <h4 style={{ margin: '0 0 0.5rem 0', color: '#374151' }}>🔒 Period Lock</h4>
                      <p style={{ margin: 0, fontSize: '0.875rem', color: '#6b7280' }}>
                        Create and lock payroll periods to prevent retroactive edits
                      </p>
                    </div>

                    {canViewTimeTrackerAudit && (
                    <div
                      data-tt-tour="admin-audit"
                      onClick={() => setAdminView('audit')}
                      style={{
                        padding: '1rem',
                        backgroundColor: '#fff7ed',
                        borderRadius: '8px',
                        border: '1px solid #fdba74',
                        cursor: 'pointer',
                      }}
                    >
                      <h4 style={{ margin: '0 0 0.5rem 0', color: '#9a3412' }}>Audit</h4>
                      <p style={{ margin: 0, fontSize: '0.875rem', color: '#9a3412' }}>
                        Who changed hours, codes, extras, crews, and locks. Restore removed rows.
                      </p>
                    </div>
                    )}

                    {/* Export - Clickable */}
                    <div 
                      data-tt-tour="admin-maestro"
                      onClick={() => { setShowAdminModal(false); setShowExportModal(true); }}
                      style={{ 
                        padding: '1rem', 
                        backgroundColor: '#f3f4f6', 
                        borderRadius: '8px',
                        border: '1px solid #e5e7eb',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#e5e7eb';
                        e.currentTarget.style.borderColor = '#6366f1';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#f3f4f6';
                        e.currentTarget.style.borderColor = '#e5e7eb';
                      }}
                    >
                      <h4 style={{ margin: '0 0 0.5rem 0', color: '#374151' }}>📤 Maestro Payroll Export</h4>
                      <p style={{ margin: 0, fontSize: '0.875rem', color: '#6b7280' }}>
                        Export payroll data in Maestro-compatible format
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Menu Footer */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: '0.75rem',
                  padding: '1rem 1.5rem',
                  borderTop: '1px solid #e5e7eb',
                  backgroundColor: '#f9fafb'
                }}>
                  <button
                    onClick={() => { setShowAdminModal(false); setAdminView('menu'); }}
                    style={{
                      padding: '0.5rem 1rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      backgroundColor: 'white',
                      color: '#374151',
                      cursor: 'pointer',
                      fontSize: '0.875rem'
                    }}
                  >
                    Close
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {showAuditModal && (
        <div
          style={{
            position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
          }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowAuditModal(false); }}
        >
          <div style={{
            backgroundColor: 'white', borderRadius: 12, width: '92%', maxWidth: 1100,
            maxHeight: '85vh', overflow: 'hidden', display: 'flex', flexDirection: 'column',
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
          }}>
            <TimeTrackerAuditLog onBack={() => setShowAuditModal(false)} />
          </div>
        </div>
      )}
      {showHelpModal && <TimeTrackerHelpModal onClose={() => setShowHelpModal(false)} />}
      {showAdminHelpModal && <TimeTrackerAdminHelpModal onClose={() => setShowAdminHelpModal(false)} />}
      {interactiveTour && (
        <TimeTrackerInteractiveTour
          variant={interactiveTour}
          onClose={() => setInteractiveTour(null)}
        />
      )}
    </div>
  );
};

export default ActualSpentLog;

